import type { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  cn,
  Gutter,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from '@crossnokaye/ux-common';

import {
  type ActionComponent as ActionComponentType,
  type ActionComponentStateConfig,
  BadgeComponentConfigVariant,
  type DropdownOption,
  type EnumOption,
  TableDataRefDisplayType,
} from '@repo/sdk/models/components';

import { PreviewChange } from '@/components/settings/preview-change';
import { ActionComponentBadge } from '@/features/equipment-browser/action-components/action-component-badge';
import { getActiveState } from '@/features/equipment-browser/conditional-evaluator/get-active-state';
import { resolveSourceUnits } from '@/features/equipment-browser/utils';
import {
  getOnOffLabel,
  parseBooleanLike,
} from '@/features/equipment-browser/utils/boolean-display';
import { formatUnits } from '@/helpers/unit-formatter/unit-formatter';

import { EquipmentBulkEditTableDropdownCell } from './equipment-bulk-edit-table-dropdown-cell';
import { EquipmentBulkEditTableInputCell } from './equipment-bulk-edit-table-input-cell';
import { EquipmentBulkEditTableSwitchCell } from './equipment-bulk-edit-table-switch-cell';

interface EquipmentBulkEditTableSettingCellProps {
  actionComponent?: ActionComponentType;
  columnId: string;
  disabled?: boolean;
  disabledByPermissions?: boolean;
  disabledTooltipText?: string;
  displayType?: TableDataRefDisplayType;
  displayUnits?: string;
  dropdownOptions?: DropdownOption[];
  enumOptions?: EnumOption[];
  permissionTooltipText?: string;
  readonlyValue?: string;
  rowId: string;
  units?: string;
}

const getEnumOrBooleanLabel = (value: unknown, enumOptions?: EnumOption[]): string | undefined => {
  const rawValue = value == null ? '' : String(value);
  // TODO(UX-3724): This currently forces On/Off labels; switch to required boolean displayUnits
  // from display_template when that migration lands. https://crossnokaye.atlassian.net/browse/UX-3724
  return enumOptions?.find((o) => o.value === rawValue)?.label ?? getOnOffLabel(value, 'title');
};

/**
 * Formats a raw form value into a human-readable display string
 * using the cell's display configuration (units, dropdown labels, etc.).
 */
const formatDisplayValue = (
  value: string,
  opts: {
    activeState?: ActionComponentStateConfig;
    displayType?: TableDataRefDisplayType;
    displayUnits?: string;
    units?: string;
    dropdownOptions?: DropdownOption[];
    enumOptions?: EnumOption[];
  }
): string => {
  const enumOrBooleanLabel = getEnumOrBooleanLabel(value, opts.enumOptions);

  // Prefer enum/boolean labels over the badge config's raw label for active badge rows.
  if (opts.activeState?.actionComponentConfig?.badgeComponentConfig) {
    return enumOrBooleanLabel ?? opts.activeState.actionComponentConfig.badgeComponentConfig.label;
  }

  if (opts.displayType === TableDataRefDisplayType.Dropdown && opts.dropdownOptions) {
    return opts.dropdownOptions.find((o) => o.value === value)?.label ?? value;
  }

  if (opts.displayType === TableDataRefDisplayType.Badge && opts.enumOptions) {
    return enumOrBooleanLabel ?? value;
  }

  if (opts.displayType === TableDataRefDisplayType.Switch && enumOrBooleanLabel) {
    return enumOrBooleanLabel;
  }

  if (
    (opts.displayType === TableDataRefDisplayType.Switch ||
      opts.displayType === TableDataRefDisplayType.Input) &&
    (opts.displayUnits || opts.units)
  ) {
    const sourceUnits = resolveSourceUnits(opts.displayUnits, opts.units);
    const formatted = formatUnits({ value, units: sourceUnits }, opts.displayUnits);
    return formatted.valueUnitsLabel ?? formatted.valueLabel ?? value;
  }

  return enumOrBooleanLabel ?? value;
};

/**
 * Builds a minimal ActionComponentStateConfig from the current form value
 * and enumOptions, so ActionComponentBadge can render a displayType=badge cell.
 */
const buildBadgeStateFromEnumOptions = (
  currentValue: string,
  enumOptions?: EnumOption[]
): ActionComponentStateConfig => {
  const matchingOption = enumOptions?.find((opt) => opt.value === currentValue);
  const fallbackLabel = getEnumOrBooleanLabel(currentValue, enumOptions) ?? currentValue;
  const parsedCurrentValue = parseBooleanLike(currentValue);
  return {
    stateName: TableDataRefDisplayType.Badge,
    actionComponentConfig: {
      kind: TableDataRefDisplayType.Badge,
      badgeComponentConfig: {
        label: matchingOption?.label ?? fallbackLabel,
        variant:
          (matchingOption?.variant as BadgeComponentConfigVariant) ??
          (parsedCurrentValue ? 'good' : 'default'),
      },
    },
  };
};

const buildDisplayBadgeState = ({
  activeState,
  currentValue,
  enumOptions,
  readonlyValue,
}: {
  activeState: ActionComponentStateConfig;
  currentValue: string;
  enumOptions?: EnumOption[];
  readonlyValue?: string;
}): ActionComponentStateConfig => {
  const actionComponentConfig = activeState.actionComponentConfig;
  const badgeConfig = actionComponentConfig?.badgeComponentConfig;
  if (!badgeConfig || !actionComponentConfig?.kind) return activeState;

  const mappedLabel =
    getEnumOrBooleanLabel(currentValue, enumOptions) ??
    getEnumOrBooleanLabel(readonlyValue, enumOptions) ??
    getEnumOrBooleanLabel(badgeConfig.label, enumOptions);
  if (!mappedLabel) return activeState;

  return {
    ...activeState,
    actionComponentConfig: {
      ...actionComponentConfig,
      kind: actionComponentConfig.kind,
      badgeComponentConfig: {
        ...badgeConfig,
        label: mappedLabel,
      },
    },
  };
};

/**
 * Unified wrapper component for all setting cell types.
 * Handles dirty-state tooltip behavior and dispatches to the correct sub-cell
 * based on activeState / displayType.
 * When a cell is dirty, shows a hover tooltip with "new value <- old value".
 *
 * Cell type resolution priority:
 * 1. Active state with badgeComponentConfig -> read-only ActionComponentBadge
 * 2. displayType fallback (dropdown, switch, badge, input, or readonly text)
 */
export const EquipmentBulkEditTableSettingCell = ({
  actionComponent,
  columnId,
  disabled,
  disabledByPermissions,
  disabledTooltipText,
  displayType,
  displayUnits,
  dropdownOptions,
  enumOptions,
  permissionTooltipText,
  readonlyValue,
  rowId,
  units,
}: EquipmentBulkEditTableSettingCellProps): ReactNode => {
  const { getFieldState, watch, formState } = useFormContext();
  const fieldName = `values.${rowId}.${columnId}`;
  const { isDirty } = getFieldState(fieldName);
  const currentValue: string = (watch(fieldName) as string) ?? '';
  const originalValue: string =
    (formState.defaultValues as Record<string, Record<string, Record<string, string>>> | undefined)
      ?.values?.[rowId]?.[columnId] ?? '';
  const showTooltip = isDirty && currentValue !== originalValue;

  // Resolve active state from actionComponent (if available)
  const activeState = actionComponent?.states?.length
    ? getActiveState({ states: actionComponent.states })
    : undefined;

  const formatOpts = {
    activeState,
    displayType,
    displayUnits,
    units,
    dropdownOptions,
    enumOptions,
  };

  let cellContent: ReactNode;

  // Priority 1: active state has a badgeComponentConfig -> render read-only badge
  if (activeState?.actionComponentConfig?.badgeComponentConfig) {
    cellContent = (
      <ActionComponentBadge
        state={buildDisplayBadgeState({ activeState, currentValue, enumOptions, readonlyValue })}
      />
    );
  } else {
    // Priority 2: fall back to displayType
    switch (displayType) {
      case TableDataRefDisplayType.Dropdown:
        cellContent = (
          <EquipmentBulkEditTableDropdownCell
            columnId={columnId}
            disabled={disabled}
            disableItems={disabledByPermissions}
            dropdownOptions={
              activeState?.actionComponentConfig?.dropdownComponentActionConfig?.options ??
              dropdownOptions
            }
            tooltipContent={disabledTooltipText ?? permissionTooltipText}
            rowId={rowId}
          />
        );
        break;
      case TableDataRefDisplayType.Switch:
        cellContent = (
          <EquipmentBulkEditTableSwitchCell columnId={columnId} disabled={disabled} rowId={rowId} />
        );
        break;
      case TableDataRefDisplayType.Badge:
        cellContent = (
          <ActionComponentBadge state={buildBadgeStateFromEnumOptions(currentValue, enumOptions)} />
        );
        break;
      case TableDataRefDisplayType.Input:
        cellContent = (
          <EquipmentBulkEditTableInputCell
            columnId={columnId}
            disabled={disabled}
            displayUnits={displayUnits}
            rowId={rowId}
            units={units}
          />
        );
        break;
      default: {
        // readonly or unknown display types: show plain text
        // TODO(UX-3724): This currently forces On/Off labels; switch to required boolean displayUnits
        // from display_template when that migration lands. https://crossnokaye.atlassian.net/browse/UX-3724
        const readonlyLabel = getOnOffLabel(readonlyValue ?? '', 'title') ?? readonlyValue ?? '--';
        cellContent = (
          <Gutter size="sm">
            <span className="text-foreground text-sm">{readonlyLabel}</span>
          </Gutter>
        );
      }
    }
  }

  return (
    <div
      className={cn('w-full self-stretch flex items-center p-2', disabled && 'cursor-not-allowed')}
    >
      {showTooltip ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <div className="w-full">{cellContent}</div>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent>
              <PreviewChange
                displayCurrent={formatDisplayValue(currentValue, formatOpts)}
                displayOriginal={formatDisplayValue(originalValue, formatOpts)}
              />
            </TooltipContent>
          </TooltipPortal>
        </Tooltip>
      ) : disabled && disabledTooltipText ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <div className="w-full">{cellContent}</div>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent>{disabledTooltipText}</TooltipContent>
          </TooltipPortal>
        </Tooltip>
      ) : (
        cellContent
      )}
    </div>
  );
};
